<script setup lang="ts">
import MainLayout from '@/common/components/MainLayout.vue';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { useRoute } from 'vue-router';
import { Button, InputText, Card, Message } from 'primevue';
import EditDashboardComponentGrid from './components/EditDashboardComponentGrid.vue';
import EditDashboardComponentDialog from './components/EditDashboardComponentDialog.vue';
import { componentEqual, type Dashboard, type DashboardComponent } from './domain/dashboard';
import { useCreateDashboardApi, type CreateDashboard } from './api/createDashboardApi';
import { useActionFailedAlert } from 'shared/problems/composables/actionFailedAlert';
import { useDashboardStore } from './stores/dashboardStore';
import { useRefreshDashboardStore } from './stores/refreshDashboardStore';
import { useUpdateDashboardApi, type UpdateDashboard } from './api/updateDashboardApi';
import { cloneObject } from 'shared/common/domain/cloneObject';

const editComponentDialog = useTemplateRef('edit-component-dialog');
const createApi = useCreateDashboardApi();
const updateApi = useUpdateDashboardApi();
const actionFailedAlert = useActionFailedAlert();

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const store = useDashboardStore();
const refreshStore = useRefreshDashboardStore();

const route = useRoute();

const current = ref<Dashboard | null>(null);
const loading = ref(false);

const name = ref<string>('');
const nameError = computed(() => {
  if (name.value === '') {
    return translations.value.error.nameIsRequired;
  }

  return undefined;
});
const nameChanged = computed(() => {
  if (!current.value) return false;
  return current.value.name !== name.value;
});

const components = ref<DashboardComponent[]>([]);
const componentsChanged = computed(() => {
  const oldDashboard = current.value;
  if (!oldDashboard) return false;
  const oldComponents = oldDashboard.components;
  const newComponents = components.value;

  for (const oldComponent of oldComponents) {
    const newComponent = newComponents.find((c) => c.id === oldComponent.id);
    if (!newComponent) return true;
    if (!componentEqual(oldComponent, newComponent)) return true;
  }

  for (const newComponent of newComponents) {
    const oldComponent = oldComponents.find((c) => c.id === newComponent.id);
    if (!oldComponent) return true;
    if (!componentEqual(oldComponent, newComponent)) return true;
  }

  return false;
});

const invalid = computed(() => nameError.value);
const changed = computed(() => nameChanged.value || componentsChanged.value);
const saveEnabled = computed(() => (current.value === null || changed.value) && !invalid.value);

onMounted(async () => {
  const dashboardId = route.params.id as string | undefined;
  if (!dashboardId) return;
  await refreshStore.refresh();
  const dashboard = store.getById(dashboardId);
  if (!dashboard) return;
  name.value = dashboard.name;
  components.value = dashboard.components;
  current.value = cloneObject(dashboard);
});

function showAddComponent() {
  editComponentDialog.value?.show({
    component: null,
  });
}

function showEditComponent(component: DashboardComponent) {
  editComponentDialog.value?.show({
    component: component,
  });
}

function addComponent(component: DashboardComponent) {
  components.value.push(component);
}

async function save() {
  try {
    loading.value = true;
    const dashboardId = current.value?.id;
    if (dashboardId) {
      const dashboard: UpdateDashboard = {
        name: name.value ?? '',
        components: components.value,
      };

      const response = await updateApi.execute({ id: dashboardId, entity: dashboard });

      if (!response.ok) {
        actionFailedAlert.show(response.details);
        return;
      }

      name.value = response.dashboard.name;
      current.value = response.dashboard;
    } else {
      const dashboard: CreateDashboard = {
        name: name.value ?? '',
        components: components.value,
      };

      const response = await createApi.execute({ entity: dashboard });

      if (!response.ok) {
        actionFailedAlert.show(response.details);
        return;
      }

      name.value = response.dashboard.name;
      current.value = response.dashboard;
    }
    await refreshStore.refresh();
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <MainLayout>
    <Card class="dashboard-card">
      <template #title>
        <span>{{
          current
            ? translations.dashboard.editPage.editTitle
            : translations.dashboard.editPage.createTitle
        }}</span>
        <Button class="add-button" @click="showAddComponent" icon="pi pi-plus" :label="translations.generic.add">
        </Button>
        <Button class="save-button" :loading="loading" :disabled="!saveEnabled" @click="save" icon="pi pi-save"
          :label="translations.generic.save">
        </Button>
      </template>
      <template #content>
        <div class="dashboard-grid">
          <div class="form container">
            <div class="field" :class="{ changed: nameChanged }">
              <label for="edit-dashboard-name">{{ translations.generic.name }}</label>
              <InputText autocomplete="off" autofocus :invalid="nameError !== undefined" id="edit-dashboard-name"
                v-model:model-value="name"></InputText>
              <Message v-if="nameError" severity="error" variant="simple">{{ nameError }}</Message>
            </div>
          </div>
          <div class="component-area">
            <EditDashboardComponentGrid v-model:components="components" ref="component-grid"
              @edit-component="showEditComponent">
            </EditDashboardComponentGrid>
          </div>

        </div>
      </template>
    </Card>
  </MainLayout>
  <EditDashboardComponentDialog ref="edit-component-dialog" @confirm="addComponent"></EditDashboardComponentDialog>
</template>

<style scoped>
:deep(.p-card-title) {
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  column-gap: 0.5rem;
  align-items: center;
}

.add-button {
  grid-column: 3;
}

.dashboard-card {
  height: 100%;
  overflow-y: hidden;
}

:deep(.p-card-body) {
  overflow-y: hidden;
  height: 100%;
}

:deep(.p-card-content) {
  overflow-y: hidden;
  height: 100%;
}

.dashboard-grid {
  display: grid;
  grid-template-rows: auto 1fr;
  height: 100%;
}

.component-area {
  height: 100%;
  overflow-y: auto;
}
</style>
